import { Component } from 'react';
import lodash from 'lodash';
import { findDOMNode } from 'react-dom';
import { connect } from 'react-redux';
import styles from './Document.scss';
import { goBack } from 'react-router-redux';
import { documentDownloadRequest } from 'part11/actions/documents';
import { buildDocumentActions } from 'common/permissions/helper';
import { push } from 'react-router-redux';

import DocumentHeaderNavigation from './DocumentHeaderNavigation/DocumentHeaderNavigation';
import DocumentBodyNavigation from './DocumentBodyNavigation/DocumentBodyNavigation';
import DocumentViewer from './DocumentViewer/DocumentViewer';
import SystemProperties from './SystemProperties/SystemProperties';
import DocumentAttributes from './DocumentAttributes/DocumentAttributes';
import DocumentSignatures from './DocumentSignatures/DocumentSignatures';
import { DocumentHistory } from './DocumentHistory/DocumentHistory';
import LoadingComponent from 'common/permissions/components/LoadingComponent';
import { Conditional } from 'common/components/layout';

import { loadMeBinderPermissions } from 'part11/actions/documents.js';

export class Document extends Component {

    constructor(props) {
        super(props);
        this.scrollAreas = {};
        this.scrollAreasList = [];
        this.state = {
            scrollArea: 'viewerArea',
            viewerAreaScrollRatio: 0
        };
        this.fromQueryValue = 'documentView';
    }

    componentDidMount() {
        this.props.loadMeBinderPermissions(this.props.binderId);
    }

    scrollEvent(e) {
        // for reference, e.target === this.scrollAreaNode;
        const { scrollTop, scrollHeight, clientHeight } = this.scrollAreaNode;
        const newRatio = scrollTop / (scrollHeight - clientHeight);
        const scrollArea = this.computeScrollArea(scrollTop);
        this.setState({
            scrollArea: scrollArea,
            viewerAreaScrollRatio: newRatio,
        });
    }

    computeScrollArea(scrollTop) {
        const fractionFromTop = 0.5;
        const criticalLine = scrollTop + this.scrollAreaNode.clientHeight * fractionFromTop;

        // Reverse the order of iteration to find the last section that's above the critical line
        const scrollAreasList = [...this.scrollAreasList].reverse();

        for (const area of scrollAreasList) {
            const scrollToPoint = this.computeScrollToHeight(area);
            if (scrollToPoint === -1) continue;

            // If the section is above the critical line, it's the active section
            if (scrollToPoint <= criticalLine) {
                return area;
            }
        }

        // Default to the viewer area if no section is found
        return 'viewerArea';
    }

    computeScrollToHeight(scrollArea) {
        const scrollAreaComponent = this.scrollAreas[scrollArea];
        if (!scrollAreaComponent) {
            return -1;
        }
        const element = findDOMNode(scrollAreaComponent);
        return element.offsetTop;
    }

    scrollToArea(scrollArea, duration = 300) {
        const scrollToPoint = this.computeScrollToHeight(scrollArea);
        if (scrollToPoint === -1) return;
        this.scrollToHeight(scrollToPoint, duration);
    }

    scrollToNextArea(duration = 300) {
        if (Math.abs(this.state.viewerAreaScrollRatio - 1) < 0.01) {
            this.scrollToArea('viewerArea');
            return;
        }
        const { scrollArea } = this.state;
        const scrollAreaIndex = this.scrollAreasList.indexOf(scrollArea);
        if (scrollAreaIndex === -1) {
            this.scrollToTop(duration);
        }
        if (scrollAreaIndex === this.scrollAreasList.length - 1) {
            this.scrollToBottom(duration);
        }
        const nextScrollArea = this.scrollAreasList[scrollAreaIndex + 1];
        this.scrollToArea(nextScrollArea);
    }

    scrollToTop(duration = 300) {
        this.scrollToHeight(0, duration);
    }

    scrollToBottom(duration = 300) {
        this.scrollToHeight(this.scrollAreaNode.scrollHeight, duration);
    }

    scrollToHeight(scrollToHeight, duration = 300) {
        const standardOffset = 10;

        const startingHeight = this.scrollAreaNode.scrollTop - standardOffset;
        const endingHeight = scrollToHeight;
        const amplitude = endingHeight - startingHeight;

        let cancel = false;
        let startingTimestamp = window.performance.now();

        const cancelHandler = (e) => {
            e.preventDefault();
            cancel = true;
            window.removeEventListener('click', cancelHandler, true);
        };
        window.addEventListener('click', cancelHandler, true);

        const step = (currentTimestamp) => {
            let durationRatio = (currentTimestamp - startingTimestamp) / duration;

            if (cancel === true) {
                durationRatio = 1;
            }

            if (durationRatio < 1) {
                this.scrollAreaNode.scrollTop =
                    startingHeight + Math.ceil(amplitude * Math.sin(0.5 * Math.PI * durationRatio)) + standardOffset;
                this.scrollEvent();
                window.requestAnimationFrame(step);
                return;
            }
            this.scrollAreaNode.scrollTop = startingHeight + amplitude + standardOffset;
            this.scrollEvent();
            window.removeEventListener('click', cancelHandler, true);
        };

        window.requestAnimationFrame(step);
    }

    subscribeToViewerArea(viewerArea) {
        if (!viewerArea || this.scrollArea) return;
        this.scrollEventHandler = lodash.throttle(this.scrollEvent.bind(this), 100);
        this.scrollArea = viewerArea;
        this.scrollAreaNode = findDOMNode(this.scrollArea);
        this.scrollAreaNode.addEventListener('scroll', this.scrollEventHandler);
    }

    unsubscribeFromViewerArea() {
        if (this.scrollArea) {
            this.scrollAreaNode.removeEventListener('scroll', this.scrollEventHandler);
            this.scrollArea = null;
            this.scrollAreaNode = null;
            this.scrollEventHandler = null;
        }
    }

    setScrollArea(key, element) {
        this.scrollAreas[key] = element;
        if (!this.scrollAreasList.includes(key)) {
            this.scrollAreasList.push(key);
        }
    }

    gotoSigningPage() {
        const { push, document, binderId, studyId } = this.props;

        if (studyId) {
            push(`/study/${studyId}/files/${document.id}/sign?from=${this.fromQueryValue}&docId=${document.id}`);
        } else {
            push(`/binder/${binderId}/files/${document.id}/sign`);
        }

    }

    gotoSendDocumentForActionForm() {
        const { push, document, binderId } = this.props;
        const location = {
            pathname: '/form/process/sdfa',
            query: {
                docid: document.id,
                iid: document.institutionId,
            },
            hash: `#${binderId}`,
        };
        push(location);
    }

    componentWillUnmount() {
        this.unsubscribeFromViewerArea();
    }

    render() {
        const {
            document,
            binderId,
            studyId,
            myDocumentTypes,
            documentDownloadRequest,
            push,
            loadDocumentHistory,
            shouldShowQuickActions,
            canManageIntegrationStatuses,
            canViewDocumentStatus,
        } = this.props;

        if (!document) {
            return <LoadingComponent />;
        }

        const { scrollArea } = this.state;
        const scrollToArea = this.scrollToArea.bind(this);

        const documentActions = shouldShowQuickActions
            ? buildDocumentActions(
                document,
                binderId,
                myDocumentTypes,
                {
                    downloadRequest: documentDownloadRequest,
                    push: push,
                    signDocument: this.gotoSigningPage.bind(this),
                    sendDocumentForAction: this.gotoSendDocumentForActionForm.bind(this),
                },
                canManageIntegrationStatuses,
                { studyId, from: this.fromQueryValue }
            )
            : null;

        return (
            <div className={styles.document} data-cy="Document">
                <DocumentHeaderNavigation
                    document={document}
                    goBack={this.props.shouldShowBackLink ? this.props.goBack : null}
                    options={documentActions}
                />
                <DocumentBodyNavigation scrollArea={scrollArea} scrollToArea={scrollToArea} document={document} />
                <DocumentScrollArea ref={(dva) => this.subscribeToViewerArea(dva)}>
                    <DocumentViewerArea ref={(dv) => this.setScrollArea('viewerArea', dv)}>
                        <DocumentViewer document={document} />
                    </DocumentViewerArea>
                    <HorizontalRule />
                    <DocumentAttributesArea ref={(daa) => this.setScrollArea('documentAttributesArea', daa)}>
                        <DocumentAttributes document={document} />
                        <SystemProperties document={document} canViewDocumentStatus={canViewDocumentStatus} />
                    </DocumentAttributesArea>
                    <HorizontalRule />
                    <DocumentSignaturesArea ref={(dsa) => this.setScrollArea('signaturesArea', dsa)}>
                        <DocumentSignatures signatures={document.signatures} />
                    </DocumentSignaturesArea>
                    <HorizontalRule />
                    <Conditional condition={canViewDocumentStatus}>
                        <DocumentHistoryArea ref={(dha) => this.setScrollArea('historyArea', dha)}>
                            <DocumentHistory
                                history={document.history}
                                loadDocumentHistory={(dateRange) =>
                                    loadDocumentHistory({ documentId: document.id, binderId }, dateRange)
                                }
                            />
                        </DocumentHistoryArea>
                        <section className={styles.documentScrollAreaSection} />
                    </Conditional>
                </DocumentScrollArea>
            </div>
        );
    }
}

export class DocumentScrollArea extends Component {
    render() {
        const { noScroll } = this.props;
        const className = noScroll ? styles.documentScrollAreaNoOverflow : styles.documentScrollArea;
        return (
            <section ref={(section) => (this.section = section)} className={className} data-cy="Document-DocumentScrollArea">
                {this.props.children}
            </section>
        );
    }
}

export class DocumentViewerArea extends Component {
    render() {
        return <section className={styles.documentViewerArea} data-cy="Document-DocumentViewerArea">{this.props.children}</section>;
    }
}

export const HorizontalRule = (props) => {
    return <hr className={styles.horizontalRule} />;
};

export class DocumentAttributesArea extends Component {
    render() {
        return <section className={styles.documentAttributesArea} data-cy="Document-DocumentAttributesArea">{this.props.children}</section>;
    }
}

export class DocumentSignaturesArea extends Component {
    render() {
        return <section className={styles.documentScrollAreaSection} data-cy="Document-DocumentSignaturesArea">{this.props.children}</section>;
    }
}

export class DocumentHistoryArea extends Component {
    render() {
        return <section className={styles.documentScrollAreaSection} data-cy="Document-DocumentHistoryArea">{this.props.children}</section>;
    }
}

export default connect(
    (state, props) => {
        const canManageIntegrationStatuses = lodash.get(state, 'me.canManageIntegrationStatuses');
        const canViewDocumentStatus = lodash.get(state, 'me.binderPermissions.viewStatus', false);

        let binderId = props.binderId || (props.params && props.params.id);
        const studyBinderId = lodash.get(state, 'study.binder.id');
        const studyId = lodash.get(state, 'study.id');

        if (studyBinderId) {
            binderId = studyBinderId;
        }

        return {
            document: props.document || lodash.get(state, `documents.${props.documentId}`),
            binderId,
            studyId,
            myDocumentTypes: lodash.get(state, 'me.documentTypes', []),
            shouldShowQuickActions: lodash.get(props, 'shouldShowQuickActions', true),
            shouldShowBackLink: lodash.get(props, 'shouldShowBackLink', true),
            canManageIntegrationStatuses,
            canViewDocumentStatus,
        };
    },
    {
        goBack,
        documentDownloadRequest,
        push,
        loadMeBinderPermissions,
    }
)(Document);
